import { Card } from '@nextui-org/react';
import CardPageContainer from '../components/CardPageContainer';
import Footer from '../components/Footer';
import Header from '../components/Header';
import InnerCardPageContainer from '../components/InnerCardPageContainer';
import Layout from '../components/Layout';

export default function GradingInfo() {
  return (
    <Layout>
      <Header description="Information about ḥadīth gradings" title="Grading Info" />
      <CardPageContainer>
        <InnerCardPageContainer>
          <Card className="single-card" role="article">
            <div className="card-body mt-2 mb-2">
              <h1 className="text-2xl font-bold">What are ḥadīth gradings?</h1>
              <p>
                One of the most significant sources of human knowledge is transmitted knowledge. Transmitted knowledge
                is defined as any knowledge relayed from one person to another orally or through a written document and
                is accessed by another person either by hearing it or reading it. The daily news you watch, the personal
                experiences your friends and family share with you, the information you are relayed at work from your
                boss, the blogs you are subscribed to and read regularly, or even a customer service representative at a
                store telling you which aisle a certain product is. In all these cases you are constantly formulating
                knowledge regarding the world and existence at large based on oral and written transmissions.
              </p>
              <br />
              <p>
                One of the most important examples of transmitted knowledge are the ḥadīth (pl. aḥadīth), the traditions
                and reports attributed to the Prophet (p) and the infallibles (a). In the words of Ayatullah Borūjerdī
                (d. 1961) the study of ḥadīth is the most noble of disciplines as it is through this discipline that one
                obeys Allah (swt), learns about His (swt) divine laws, learns the Qurān, its revelation, exegesis and
                its hidden and concealed meanings. It is through this discipline that one protects the religion, and the
                pillars of Islam are strengthened. If it were not for the ḥadīth, and those who heard the narrations,
                memorized them, wrote them down, transmitted them, and took guardianship of them, the pillars of
                religion would have collapsed, just as the Imam (a) himself affirms when he said, “If there was no
                Zurārah and others like him, the narrations of my father would have been wiped out.
              </p>
              <br />
              <p>
                Given the importance of the ḥadīth, it then only behooves one to have an epistemic framework by which
                they can judge, ascertain, and verify the report to be an authentic reflection of what the Prophet (p)
                and the infallibles (a) had imparted. When one comes across transmitted knowledge in their daily life,
                by what criteria do they judge and ascertain this knowledge? Do they only affirm this transmitted
                knowledge if they personally attain certainty or a strong conviction after hearing or reading something,
                or is one’s personal conviction irrelevant and instead the mere fact that a trustworthy figure is
                informing them of something, or that a report exists in a certain book they trust suffices for them to
                believe in it and act upon it? While the answers to these and other similar thought-provoking questions
                are extensively discussed by Muslim scholars, regardless of what you determine as a criterion, one of
                the key factors that helps one fulfil their criterion is the study and knowledge of individuals who are
                memorizing, writing, and transmitting the ḥadīth, albeit the need may vary. The particular discipline
                that studies the qualities of individuals recording and transmitting the ḥadīth, whether they are
                reliable or not, is called ‘Ilm al-Rijāl, and the discipline that studies the descriptions of the ḥadīth
                itself, whether they are authentic or unauthentic, is called ‘Ilm al-Dirāyah. While these disciplines
                can assist us in generally organizing and categorizing the narrators, scribes, authors of various ḥadīth
                books, and the ḥadith themselves, it is imperative to note that there are technicalities involved in
                accepting and rejecting a ḥadīth beyond these mere descriptions.
              </p>
              <br />
              <p>
                In other words, it is very much possible for one or more transmitters of a ḥadīth to be known as an
                untrustworthy person, and subsequently for the ḥādīth to be classified as weak, however, when put
                alongside other similar traditions or in light of other contextual indicators, its content could be
                attributed to the Prophet (p) or the infallibles (a) with conviction. Likewise, it is very much possible
                for the transmitters of a ḥadīth to be trustworthy, and subsequently for the ḥadīth to be classified as
                authentic, yet based on other contextual indicators, one may conclude that its contents cannot be
                attributed to the Prophet (p) or the infallibles with conviction. In fact, at times the contrary
                evidence may be strong enough to even conclude that the report was a fabrication.
              </p>
              <br />
              <p>
                As an aid for researchers, students and scholars who recognize the significance of the aforementioned
                questions and discussions, Thaqalayn.net has made a humble effort to publish the gradings of every
                particular ḥadīth according to the opinion of a number of scholars. For non-specialists, these gradings
                should be taken with a grain of salt and one should not be inclined to derive any conclusions regarding
                the narrators or contents of the ḥadīth based on them.
              </p>
              <br />
              <p>
                Currently, these include gradings of ‘Allāmah Majlisī (d. 1699) and Shaykh Bāqir Behbūdī (d. 2015) for
                the aḥadīth in al-Kāfī. And you may also find the gradings of Shaykh Hādī al-Najafī and Shaykh Āṣif
                al-Moḥsini (d. 2019) sporadically.
              </p>
            </div>
          </Card>
        </InnerCardPageContainer>
        <div className="mt-10" />
      </CardPageContainer>
      <Footer />
    </Layout>
  );
}
